import type {
    QuickFilterConfig,
    QuickFilterState,
} from 'shared/filter_pane/QuickFilterPane';
import { arraysAreSame } from 'utils/array';

/**
 * Determines if a quick filter preset exactly matches the current filter state.
 *
 * For array filters, uses order-insensitive comparison (Sets),
 * meaning [A, B] equals [B, A].
 *
 * For non-array filters, uses strict equality comparison.
 *
 * @param filter - The quick filter configuration
 * @param currentFilters - The current filter state
 * @returns true if the filter preset exactly matches the current state
 *
 * @example
 * // Array filter with order-insensitive comparison
 * const filter = { value: ['critical', 'high'], isArray: true, field: 'priority' };
 * const state = { priority: ['high', 'critical'] };
 * isQuickFilterActive(filter, state); // true (order doesn't matter)
 */
export function isQuickFilterActive<T extends QuickFilterState>(
    filter: QuickFilterConfig<T>,
    currentFilters: T
): boolean {
    const currentValue = currentFilters[filter.field];

    if (filter.isArray) {
        return Array.isArray(currentValue) && arraysAreSame(currentValue, filter.value);
    }

    return currentValue === filter.value;
}
